import {
  type OperationResponse,
  type Parameters,
  type UnknownParamsObject,
} from "../../common.ts";
import { omitPathParameters, replacePathParameters } from "../../utils";
import type { AxiosInstance, AxiosRequestConfig } from "axios";
import type * as Paths from "./paths";

interface OperationMethods {
  "getCatalog"(
    parameters?: Parameters<Paths.GetCatalog.QueryParameters> | null,
    data?: any,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.GetCatalog.Responses.$200>;
  "getCategories"(
    parameters?: Parameters<Paths.GetCategories.QueryParameters> | null,
    data?: any,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.GetCategories.Responses.$200>;
  "supplier-product-hub.store"(
    parameters?: Parameters<UnknownParamsObject> | null,
    data?: Paths.SupplierProductHubStore.RequestBody,
    config?: AxiosRequestConfig,
  ): OperationResponse<any>;
  "supplier-product-hub.show"(
    parameters?: Parameters<Paths.SupplierProductHubShow.PathParameters> | null,
    data?: any,
    config?: AxiosRequestConfig,
  ): OperationResponse<any>;
  "supplier-product-hub.update"(
    parameters?: Parameters<Paths.SupplierProductHubUpdate.PathParameters> | null,
    data?: Paths.SupplierProductHubUpdate.RequestBody,
    config?: AxiosRequestConfig,
  ): OperationResponse<any>;
  "supplier-product-hub.destroy"(
    parameters?: Parameters<Paths.SupplierProductHubDestroy.PathParameters> | null,
    data?: any,
    config?: AxiosRequestConfig,
  ): OperationResponse<any>;
  "supplierProductHub.uploadFile"(
    parameters?: Parameters<UnknownParamsObject> | null,
    data?: Paths.SupplierProductHubUploadFile.RequestBody,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.SupplierProductHubUploadFile.Responses.$200>;
  "addProductToFavouriteExonnHub"(
    parameters?: Parameters<UnknownParamsObject> | null,
    data?: Paths.AddProductToFavouriteExonnHub.RequestBody,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.AddProductToFavouriteExonnHub.Responses.$200>;
  "removeProductFromFavouriteExonnHub"(
    parameters?: Parameters<UnknownParamsObject> | null,
    data?: Paths.RemoveProductFromFavouriteExonnHub.RequestBody,
    config?: AxiosRequestConfig,
  ): OperationResponse<Paths.RemoveProductFromFavouriteExonnHub.Responses.$200>;
}

export const getSupplierProductHubOperations = (client: AxiosInstance) => {
  const getCatalog: OperationMethods["getCatalog"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/supplier-product-hub";
    return client.get(replacePathParameters(url, parameters), {
      params: omitPathParameters(parameters, url),
      data: data,
      ...config,
    });
  };

  const getCategories: OperationMethods["getCategories"] = (
    parameters,
    data,
    config,
  ) => {
    const url = "/supplier-product-hub/categories/tree";
    return client.get(replacePathParameters(url, parameters), {
      params: omitPathParameters(parameters, url),
      data: data,
      ...config,
    });
  };
  const supplierProductHubStore: OperationMethods["supplier-product-hub.store"] =
    (parameters, data, config) => {
      const url = "/supplier-product-hub";
      return client.post(replacePathParameters(url, parameters), data, {
        params: omitPathParameters(parameters, url),
        ...config,
      });
    };
  const supplierProductHubShow: OperationMethods["supplier-product-hub.show"] =
    (parameters, data, config) => {
      const url = "/supplier-product-hub/{supplier_product_hub}";
      return client.get(replacePathParameters(url, parameters), {
        params: omitPathParameters(parameters, url),
        data: data,
        ...config,
      });
    };
  const supplierProductHubUpdate: OperationMethods["supplier-product-hub.update"] =
    (parameters, data, config) => {
      const url = "/supplier-product-hub/{supplier_product_hub}";
      return client.put(replacePathParameters(url, parameters), data, {
        params: omitPathParameters(parameters, url),
        ...config,
      });
    };
  const supplierProductHubDestroy: OperationMethods["supplier-product-hub.destroy"] =
    (parameters, data, config) => {
      const url = "/supplier-product-hub/{supplier_product_hub}";
      return client.delete(replacePathParameters(url, parameters), {
        params: omitPathParameters(parameters, url),
        data: data,
        ...config,
      });
    };
  const supplierProductHubUploadFile: OperationMethods["supplierProductHub.uploadFile"] =
    (parameters, data, config) => {
      const url = "/supplier-product-hub/file";
      return client.post(replacePathParameters(url, parameters), data, {
        params: omitPathParameters(parameters, url),
        ...config,
      });
    };

  const addProductToFavouriteExonnHub: OperationMethods["addProductToFavouriteExonnHub"] =
    (parameters, data, config) => {
      const url = "/supplier-product-hub/favourites/add";
      return client.post(replacePathParameters(url, parameters), data, {
        params: omitPathParameters(parameters, url),
        ...config,
      });
    };
  const removeProductFromFavouriteExonnHub: OperationMethods["removeProductFromFavouriteExonnHub"] =
    (parameters, data, config) => {
      const url = "/supplier-product-hub/favourites/remove";
      return client.post(replacePathParameters(url, parameters), data, {
        params: omitPathParameters(parameters, url),
        ...config,
      });
    };

  return {
    getCatalog,
    getCategories,
    "supplier-product-hub.store": supplierProductHubStore,
    "supplier-product-hub.show": supplierProductHubShow,
    "supplier-product-hub.update": supplierProductHubUpdate,
    "supplier-product-hub.destroy": supplierProductHubDestroy,
    "supplierProductHub.uploadFile": supplierProductHubUploadFile,
    addProductToFavouriteExonnHub: addProductToFavouriteExonnHub,
    removeProductFromFavouriteExonnHub: removeProductFromFavouriteExonnHub,
  };
};
